import type { LucideIcon } from 'lucide-react';
import { Store, Users, Newspaper, Boxes, Database, Shield, HelpCircle } from 'lucide-react';

export interface SearchPattern {
  test: RegExp;
  label: string;
  getRoute: (match: RegExpMatchArray) => string;
  priority: number;
}

export interface SearchablePage {
  id: string;
  label: string;
  keywords?: string[];
  path: string;
  icon?: LucideIcon;
}

export interface SearchProvider {
  id: string;
  label: string;
  icon: LucideIcon;
  capability: { domain: string; action: string };
  patterns?: SearchPattern[];
  pages: SearchablePage[];
}

// Registry — composed from all modules.
// Modules will add their own providers as they're built (E3-E8).
export const searchProviders: SearchProvider[] = [
  {
    id: 'stores',
    label: 'Restaurants',
    icon: Store,
    capability: { domain: 'stores', action: 'read' },
    patterns: [
      {
        test: /^\d{4,6}$/,
        label: 'Go to Store',
        getRoute: (match) => `/stores/${encodeURIComponent(match[0])}`,
        priority: 100,
      },
    ],
    pages: [
      {
        id: 'store-search',
        label: 'Store Search',
        path: '/stores',
        keywords: ['champs', 'store number', 'find store', 'browse'],
        icon: Store,
      },
      {
        id: 'store-create-test',
        label: 'Create Test Restaurants',
        path: '/stores/create-test',
        keywords: ['create test', 'test store', 'test restaurant'],
      },
      {
        id: 'store-timezone',
        label: 'Timezone Tools',
        path: '/stores/timezone',
        keywords: ['timezone', 'tz', 'validate', 'correct', 'autofill'],
      },
      {
        id: 'store-equipment',
        label: 'Equipment & Routines',
        path: '/stores/equipment',
        keywords: ['equipment', 'routine', 'checklist', 'brand'],
      },
    ],
  },
  {
    id: 'users',
    label: 'Users',
    icon: Users,
    capability: { domain: 'users', action: 'read' },
    patterns: [
      {
        test: /^[A-Z]{2}\d{3,}$/i,
        label: 'Go to User',
        getRoute: (match) => `/users/${match[0].toUpperCase()}`,
        priority: 90,
      },
    ],
    pages: [
      {
        id: 'user-search',
        label: 'User Search',
        path: '/users',
        keywords: ['yum id', 'find user', 'profile', 'browse'],
        icon: Users,
      },
    ],
  },
  {
    id: 'feeds',
    label: 'Content',
    icon: Newspaper,
    capability: { domain: 'feeds', action: 'read' },
    pages: [
      {
        id: 'feed-search',
        label: 'Feed Search',
        path: '/feeds',
        keywords: ['feed', 'post', 'content', 'engagement'],
        icon: Newspaper,
      },
    ],
  },
  {
    id: 'microapps',
    label: 'MicroApps',
    icon: Boxes,
    capability: { domain: 'microapps', action: 'read' },
    pages: [
      {
        id: 'microapp-list',
        label: 'MicroApp Config',
        path: '/microapps',
        keywords: ['microapp', 'config', 'feature flag'],
        icon: Boxes,
      },
    ],
  },
  {
    id: 'queries',
    label: 'Query Explorer',
    icon: Database,
    capability: { domain: 'queries', action: 'read' },
    pages: [
      {
        id: 'query-library',
        label: 'Query Library',
        path: '/queries/library',
        keywords: ['query', 'pre-built', 'parameterized'],
        icon: Database,
      },
      {
        id: 'sql-console',
        label: 'SQL Console',
        path: '/queries/console',
        keywords: ['sql', 'console', 'database', 'select'],
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: Shield,
    capability: { domain: 'admin', action: 'read' },
    pages: [
      {
        id: 'admin-users',
        label: 'User Management',
        path: '/admin/users',
        keywords: ['admin', 'roles', 'permissions', 'override'],
        icon: Shield,
      },
      {
        id: 'admin-audit',
        label: 'Audit Log',
        path: '/admin/audit',
        keywords: ['audit', 'log', 'activity', 'who did what'],
      },
      {
        id: 'admin-health',
        label: 'System Health',
        path: '/admin/health',
        keywords: ['health', 'status', 'database', 'connection'],
      },
      {
        id: 'admin-analytics',
        label: 'Platform Analytics',
        path: '/admin/analytics',
        keywords: ['analytics', 'dau', 'adoption', 'kpi'],
      },
    ],
  },
  {
    id: 'help',
    label: 'Help',
    icon: HelpCircle,
    capability: { domain: 'ai', action: 'read' },
    pages: [
      {
        id: 'help-guides',
        label: 'Help & Guides',
        path: '/help',
        keywords: ['help', 'guide', 'how to', 'documentation'],
        icon: HelpCircle,
      },
    ],
  },
];
